'use client';

import Image from 'next/image';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import { useGooglePlayUrl } from '@/lib/hooks/useGooglePlayUrl';
import { Icon } from '@/lib/ui-kit/icons';

/**
 * "Get the app" banner shown on phones and tablets — i.e. below the `sm`
 * breakpoint, exactly where the header's QR download button is hidden.
 * Dismissal is in-memory only: closing hides it for the current view, and it
 * shows again on reload.
 */
export function AppDownloadBanner() {
  const t = useTranslations('banner');
  const tFooter = useTranslations('footer');
  const [dismissed, setDismissed] = useState(false);
  const googlePlayUrl = useGooglePlayUrl();

  if (dismissed) return null;

  const handleDismiss = () => setDismissed(true);

  return (
    <div
      className="flex w-full items-center justify-between px-4 py-3 min-[600px]:px-6 sm:hidden
        bg-[radial-gradient(circle_at_100%_24%,#9AC4FF_0%,#FFFFFF_100%)]
        dark:bg-[radial-gradient(circle_at_100%_24%,rgba(0,50,125,1)_0%,rgba(0,9,23,1)_100%)]"
    >
      <div className="flex min-w-0 items-center gap-1">
        <button
          type="button"
          onClick={handleDismiss}
          aria-label={t('dismiss')}
          className="text-v2-text-secondary flex size-4 shrink-0 items-center justify-center
            transition-opacity hover:opacity-70"
        >
          <Icon name="CloseFilled" size={16} />
        </button>
        <div className="flex min-w-0 items-center gap-2">
          <Image
            src="/outpoll-app-icon.webp"
            alt=""
            width={36}
            height={36}
            className="size-9 shrink-0 rounded-[10px]"
          />
          <div className="flex min-w-0 flex-col justify-center gap-1.5">
            <span className="text-v2-text-primary truncate text-[13px] font-[500] leading-4 tracking-[-0.13px]">
              {t('getApp')}
            </span>
            <span className="text-v2-text-secondary dark:text-grey-200 truncate text-[11px] font-normal leading-[115%]">
              {t('tagline')}
            </span>
          </div>
        </div>
      </div>
      <a
        href={googlePlayUrl}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={tFooter('getItOnGooglePlay')}
        className="ml-3 shrink-0"
      >
        <Image
          src="/download-google-play.svg"
          alt={tFooter('getItOnGooglePlay')}
          width={104}
          height={30}
          className="h-[30px] w-auto"
        />
      </a>
    </div>
  );
}
